export class CrmContactApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  async getContacts(params) {
    return this.#http.get('/api/contacts', { params, paramsSerializer: { indexes: null } });
  }

  async getContactDuplicates({ page, pageSize }) {
    return this.#http.get('/api/contacts/duplicates', { params: { page, pageSize } });
  }

  async mergeContacts({ contactId, sourceContactId }) {
    return this.#http.post(`/api/contacts/${contactId}/merge`, { sourceContactId });
  }

  async getContactById(contactId) {
    return this.#http.get(`/api/contacts/${contactId}`);
  }

  async getContactByBorrowerId({ borrowerId }) {
    return this.#http.get(`/api/contacts/by-borrower/${borrowerId}`);
  }

  async linkContactToBorrower({ contactId, borrowerId, applicationId }) {
    return this.#http.post(`/api/contacts/${contactId}/borrower-links`, {
      borrowerId,
      applicationId,
    });
  }

  async unlinkContactFromBorrower({ contactId, borrowerId }) {
    return this.#http.delete(`/api/contacts/${contactId}/borrower-links/${borrowerId}`);
  }

  async getContactsByApplication({ applicationId }) {
    return this.#http.get(`/api/applications/${applicationId}/contacts`);
  }

  async getContactsByLead({ leadId }) {
    return this.#http.get(`/api/leads/${leadId}/contacts`);
  }

  async createContact(body) {
    return this.#http.post('/api/contacts', body);
  }

  async updateContact({ contactId, ...body }) {
    return this.#http.put(`/api/contacts/${contactId}`, body);
  }

  async updateContactParent({ contactId, parentContactId }) {
    return this.#http.put(`/api/contacts/${contactId}/parent`, { parentContactId });
  }

  async getContactChildren({ contactId, page = 1, pageSize = 50 }) {
    return this.#http.get(`/api/contacts/${contactId}/children`, { params: { page, pageSize } });
  }

  async linkContactToSubCompany({ contactId, subCompanyId }) {
    return this.#http.post(`/api/contacts/${contactId}/subcompany-links`, { subCompanyId });
  }

  async unlinkContactFromSubCompany({ contactId, subCompanyId }) {
    return this.#http.delete(`/api/contacts/${contactId}/subcompany-links/${subCompanyId}`);
  }

  async getContactSubCompanyLinks({ contactId }) {
    return this.#http.get(`/api/contacts/${contactId}/subcompany-links`);
  }

  async updateContactLifecycle({ contactId, newStage }) {
    return this.#http.put(`/api/contacts/${contactId}/lifecycle`, { newStage });
  }

  async deleteContact(contactId) {
    return this.#http.delete(`/api/contacts/${contactId}`);
  }

  async getContactNotes({ contactId }) {
    return this.#http.get(`/api/contacts/${contactId}/notes`);
  }

  async createContactNote({ contactId, ...body }) {
    return this.#http.post(`/api/contacts/${contactId}/notes`, body);
  }

  async updateContactNote({ contactId, noteId, ...body }) {
    return this.#http.put(`/api/contacts/${contactId}/notes/${noteId}`, body);
  }

  async deleteContactNote({ contactId, noteId }) {
    return this.#http.delete(`/api/contacts/${contactId}/notes/${noteId}`);
  }

  async getContactActivityLog({ contactId, params }) {
    return this.#http.get(`/api/contacts/${contactId}/activities`, { params });
  }

  async logContactActivity({ contactId, ...body }) {
    return this.#http.post(`/api/contacts/${contactId}/activities`, body);
  }

  async getContactApplicationLinks({ contactId }) {
    return this.#http.get(`/api/contacts/${contactId}/links`);
  }

  async linkContactToApplication({ contactId, applicationId, role, primaryBorrowerId }) {
    return this.#http.post(`/api/contacts/${contactId}/links/applications`, {
      applicationId,
      role,
      primaryBorrowerId,
    });
  }

  async unlinkContactFromApplication({ contactId, applicationId }) {
    return this.#http.delete(`/api/contacts/${contactId}/links/applications/${applicationId}`);
  }

  async assignContactTag({ contactId, tagId }) {
    return this.#http.post(`/api/contacts/${contactId}/tags`, { tagId });
  }

  async removeContactTag({ contactId, tagId }) {
    return this.#http.delete(`/api/contacts/${contactId}/tags/${tagId}`);
  }

  async createApplicationForContact({ contactId, isTest = false }) {
    return this.#http.post(`/api/contacts/${contactId}/create-application`, { isTest });
  }

  async getContactAddresses(contactId) {
    return this.#http.get(`/api/contacts/${contactId}/addresses`);
  }

  async addContactAddress(contactId, dto) {
    return this.#http.post(`/api/contacts/${contactId}/addresses`, dto);
  }

  async updateContactAddress(contactId, addressId, dto) {
    return this.#http.put(`/api/contacts/${contactId}/addresses/${addressId}`, dto);
  }

  async claimContact(contactId) {
    return this.#http.post(`/api/contacts/${contactId}/claim`);
  }

  // PUT /api/contacts/{id} is a partial update — a null assignedUserId there reads as "field absent"
  // and leaves the owner untouched, so unassigning needs its own endpoint (DEV-6118).
  async unassignContactOwner(contactId) {
    return this.#http.delete(`/api/contacts/${contactId}/owner`);
  }
}
